// CONSUMO MEDIDO: tudo que vive sob /v1/costs/*. É o que a organização GASTOU,
// medido a partir de `call_usage` — não o que ela contratou (isso é ./plans.js).
//
// Três leituras que as três telas de Financeiro fazem, e um só recorte:
//
//   - /v1/costs/summary (+ /rates): custo operacional por dia, telefonia vs IA.
//     O back guarda QUANTIDADES e multiplica pela tarifa na leitura, então mudar
//     uma tarifa recalcula o histórico inteiro, inclusive o passado;
//   - /v1/costs/summary-by-user: o ASSENTO por usuário ativo. Não é o mesmo
//     dinheiro do summary — mas é a MESMA janela de tempo, e por isso divide com
//     ele o `CostSummaryQuery`. Separar os dois em módulos faria um importar o
//     tipo do outro para dizer "de quando até quando", que é a definição de
//     recorte errado;
//   - /v1/costs/billing: a conta do mês já consolidada (mensalidade + assento,
//     minuto e armazenamento extras). Mora aqui pela rota e pela origem do dado
//     (a mesma medição), mesmo que a tela que a lê fale de fatura.
//
// O que NÃO está aqui: plano, assinatura, checkout e portal do Stripe. Aquilo é
// contrato (./plans.js) e este módulo não o importa — as duas metades só se
// encontram na tela, nunca no request.
import { api, toApiError } from "./client.js";

// As tarifas são SÓ LEITURA: vêm do env do back (preço nosso, o mesmo para toda
// organização) e não há PUT — a tabela cost_rates, que guardava tarifa por org,
// foi dropada. 0 = ainda não definida.
export interface CostRates {
  telephonyPerMin: number;
  ttsPer1kChars: number;
  llmPer1mPromptTokens: number;
  llmPer1mCompletionTokens: number;
  gpuPerAiMin: number;
}

export interface CostDay {
  day: string;
  telephony: number;
  ai: number;
  total: number;
}

export interface CostSummary {
  totals: {
    telephony: number;
    tts: number;
    llm: number;
    gpu: number;
    total: number;
    // R$ que o cache de TTS economizou (chars cacheados × tarifa). NÃO entra no
    // total — é custo evitado, não gasto.
    ttsCachedSaved: number;
    calls: number;
    answeredCalls: number;
    aiCalls: number;
  };
  // Uso cru agregado da IA no período (quantidades medidas em call_usage), p/ a
  // tela detalhar o consumo de voz/IA além do R$.
  usage: {
    ttsChars: number;
    ttsCachedChars: number;
    ttsAudioS: number;
    sttAudioS: number;
    llmPromptTokens: number;
    llmCompletionTokens: number;
    aiAudioS: number;
  };
  perDay: CostDay[];
  // Dia da 1ª ligação com IA medida (null = nenhuma). Antes disso não há custo de
  // IA registrado — a tela avisa em vez de exibir zero como se fosse economia.
  aiSince: string | null;
  rates: CostRates;
}

export interface CostSummaryQuery {
  from?: string;
  to?: string;
}

export async function getCostSummary(params: CostSummaryQuery = {}): Promise<CostSummary> {
  const { data, error } = await api<CostSummary>("/v1/costs/summary", { query: params });
  if (error) throw toApiError("GET /v1/costs/summary", error);
  return data;
}

export async function getCostRates(): Promise<CostRates> {
  const { data, error } = await api<CostRates>("/v1/costs/rates");
  if (error) throw toApiError("GET /v1/costs/rates", error);
  return data;
}

// --- Custo por usuário (/financial/costs-by-user) -----------------------------
// ASSENTO: o preço da licença por usuário ATIVO da org, o mesmo para todos
// (definido no back por env, SEAT_PRICE_BRL). NÃO é telefonia+IA — isso é o
// CostSummary da tela de Minutagem. Vem todo membro ativo, inclusive quem não
// ligou no período (calls 0): o assento é devido pelo acesso, não pelo uso, e
// por isso entra CHEIO, sem rateio pelo período selecionado.
export interface CostByUser {
  userId: string;
  userName: string | null;
  calls: number;
  answeredCalls: number;
  // Assento em R$ deste usuário.
  total: number;
}

// Ficava no FIM de api.ts, dezenas de linhas depois da seção e do tipo que ela
// declarava (logo abaixo do LGPD, sem parentesco nenhum). Aqui volta para junto
// do `CostByUser`; o recorte não mudou, só a distância.
export async function getCostSummaryByUser(params: CostSummaryQuery = {}): Promise<CostByUser[]> {
  const { data, error } = await api<CostByUser[]>("/v1/costs/summary-by-user", {
    query: params,
  });
  if (error) throw toApiError("GET /v1/costs/summary-by-user", error);
  return data;
}

// --- Conta do mês (/financial/billing) ----------------------------------------
// A conta do mês virou PLANO: mensalidade + assento, minuto e armazenamento
// extras. O
// consumo de IA (TTS/LLM/GPU) saiu da fatura do cliente e vive só nas telas de
// Custos/Minutagem, como visão de margem.
export interface BillingMonth {
  // "2026-08", em America/Sao_Paulo.
  month: string;
  plan: { name: string; status: string } | null;
  monthly: number;
  seats: {
    // Maior número de membros SIMULTÂNEOS no mês (não o do fechamento).
    peak: number;
    included: number;
    extra: number;
    total: number;
  };
  minutes: { used: number; included: number; extra: number; total: number };
  storage?: {
    usedGbMonth: number;
    includedGbMonth: number;
    extraGbMonth: number;
    total: number;
  };
  // Mês fechado: o valor vem da fatura do Stripe e não muda mais.
  frozen: boolean;
  invoice: { status: string; hostedUrl: string | null; pdfUrl: string | null } | null;
  total: number;
}

export async function getBilling(months = 12): Promise<BillingMonth[]> {
  const { data, error } = await api<BillingMonth[]>("/v1/costs/billing", {
    query: { months },
  });
  if (error) throw toApiError("GET /v1/costs/billing", error);
  return data;
}
